import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserAppTransaction } from '@dxb-web-ui-framework/iam/domain/AccessControl/entities';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import {
  Container,
  Paper,
  CardHeading,
  makeStyles,
  Tabs,
  Tab,
  Box,
  Skeleton,
} from '@dxb-web-ui-framework/design-system';
import { Helmet } from 'react-helmet';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { LayoutBuilder } from '../../../content-composer/src/components/Layouts';
import { AuthorizationsTable, TransactionsTable } from '../components';
import { AuthorizationsPayrollTableWrapper } from '../components/Payroll';
import {
  AuthorizationProvider,
  TransactionsProvider,
  useNotificationsAuthorizationsList,
  useNotificationsTransactionsList,
  useNotificationsPayrollList,
  useNotificationsLoadingState,
} from '../contexts';
import { MobileHeader } from '../../../content-composer/src/components/Partials/Header';
import { AuthorizationPayrollProvider } from '../contexts/Payroll/AuthorizationPayrollProvider';
import { useUserInfoIsUserAllowedAction } from '../../../userProfile/src/contexts/UserInfo/hooks';

export enum TabValues {
  authorizations = 'AUTHORIZATIONS',
  transactions = 'TRANSACTIONS',
  payrolls = 'PAYROLLS',
}

const useStyles = makeStyles(theme => ({
  tabsContainer: {
    paddingLeft: theme.spacing(4),
    paddingTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
  },
  tableActionsContainer: {
    display: 'flex',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(4),
    marginLeft: theme.spacing(4),
    paddingBottom: theme.spacing(1),
    justifyContent: 'flex-start',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(0),
      marginBottom: theme.spacing(1),
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(3),
    },
  },
  typographyValue: {
    fontSize: theme.typography.body2.fontSize,
  },
  tableActionButton: {
    padding: theme.spacing(0),
  },
}));

const AuthorizationsListPage: FC<GenericObject> = ({ pageConfig }) => {
  const isUserEntitledViewAuthorizationsList = useUserInfoIsUserAllowedAction(
    UserAppTransaction.PendingAuthorizationsList,
  );
  const isUserEntitledToViewPendingPayrollList = useUserInfoIsUserAllowedAction(
    [UserAppTransaction.PendingAuthorizationsList, UserAppTransaction.ImportPayrollFile],
    'AND',
  );

  const authorizationsList = useNotificationsAuthorizationsList();
  const transactionsList = useNotificationsTransactionsList();
  const payrollList = useNotificationsPayrollList();
  const pendingAuthorizationsLoading = useNotificationsLoadingState('isLoadingPendingAuthorizations');

  const [activeTab, setActiveTab] = useState<TabValues>(
    isUserEntitledViewAuthorizationsList ? TabValues.authorizations : TabValues.transactions,
  );
  const { t } = useTranslation();
  const { isExtraSmallScreen: isMobile } = useScreenDetector();

  const onChangeTab = useCallback((e: any, newValue: TabValues) => {
    setActiveTab(newValue);
  }, []);

  const classes = useStyles();

  return (
    <>
      <Helmet>
        <title>{t(pageConfig.title)}</title>
      </Helmet>
      <MobileHeader />
      <LayoutBuilder layout={pageConfig.layout}>
        <Container maxWidth={!isMobile ? false : 'md'} disableGutters={!isMobile}>
          <CardHeading heading={t('authorizationsListPage.CardHeading.Heading')} hideOnMobile={true} />
        </Container>
        <Paper elevation={isMobile ? 0 : 1}>
          <Box className={classes.tabsContainer}>
            <Tabs value={activeTab} onChange={onChangeTab} aria-label={t('authorizationsListPage.Tabs.AriaLabel')}>
              {isUserEntitledViewAuthorizationsList && (
                <Tab
                  value={TabValues.authorizations}
                  label={
                    pendingAuthorizationsLoading ? (
                      <Skeleton width={100} />
                    ) : (
                      t('authorizationsListPage.Tab.Authorizations', {
                        value: authorizationsList?.length,
                        plural: authorizationsList?.length === 1 ? '' : 's',
                      })
                    )
                  }
                />
              )}
              <Tab
                value={TabValues.transactions}
                label={
                  pendingAuthorizationsLoading ? (
                    <Skeleton width={100} />
                  ) : (
                    t('authorizationsListPage.Tab.Transactions', {
                      value: transactionsList?.length,
                      plural: transactionsList?.length === 1 ? '' : 's',
                    })
                  )
                }
              />
              {isUserEntitledToViewPendingPayrollList && (
                <Tab
                  value={TabValues.payrolls}
                  label={
                    pendingAuthorizationsLoading ? (
                      <Skeleton width={100} />
                    ) : (
                      t('authorizationsListPage.Tab.Payrolls', {
                        value: payrollList?.length,
                        plural: payrollList?.length === 1 ? '' : 's',
                      })
                    )
                  }
                />
              )}
            </Tabs>
          </Box>
          {activeTab === TabValues.authorizations && isUserEntitledViewAuthorizationsList && (
            <AuthorizationProvider authorizationsList={authorizationsList}>
              <AuthorizationsTable classes={classes} />
            </AuthorizationProvider>
          )}
          {activeTab === TabValues.transactions && (
            <TransactionsProvider transactionsList={transactionsList}>
              <TransactionsTable />
            </TransactionsProvider>
          )}
          {activeTab === TabValues.payrolls && (
            <AuthorizationPayrollProvider payrollList={payrollList}>
              <AuthorizationsPayrollTableWrapper classes={classes} />
            </AuthorizationPayrollProvider>
          )}
        </Paper>
      </LayoutBuilder>
    </>
  );
};

export { AuthorizationsListPage };

export default AuthorizationsListPage;
